import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeader = () => {
  const t = localStorage.getItem('admin_token')
         || localStorage.getItem('token')
         || localStorage.getItem('access_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export function useLegalcoopFinance() {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [counters, setCounters] = useState({});
  const [filterStatus, setFilterStatus] = useState('all');

  const [createOpen, setCreateOpen] = useState(false);
  const [createEmail, setCreateEmail] = useState('');
  const [createName, setCreateName] = useState('');
  const [createCcmiRef, setCreateCcmiRef] = useState('');
  const [createNotes, setCreateNotes] = useState('');
  const [createValidity, setCreateValidity] = useState(30);
  const [createSubmitting, setCreateSubmitting] = useState(false);

  const [linkResult, setLinkResult] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus && filterStatus !== 'all') params.status = filterStatus;
      const res = await axios.get(`${API}/legalcoop/finance/devis`, { params, headers: getAuthHeader() });
      setItems(res.data?.items || []);
      setCounters(res.data?.counters || {});
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur chargement devis');
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const submitCreate = async () => {
    const email = (createEmail || '').trim().toLowerCase();
    if (!email.includes('@') || !email.includes('.')) {
      toast.error('Email invalide');
      return;
    }
    setCreateSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/legalcoop/finance/devis`,
        {
          client_email: email,
          client_name: createName || null,
          ccmi_project_reference: createCcmiRef || null,
          notes: createNotes || null,
          validity_days: Number(createValidity) || 30,
        },
        { headers: getAuthHeader() },
      );
      toast.success(`Devis ${res.data?.devis?.quote_number} créé`);
      setCreateOpen(false);
      setCreateEmail(''); setCreateName(''); setCreateCcmiRef(''); setCreateNotes(''); setCreateValidity(30);
      fetchList();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur création devis');
    } finally {
      setCreateSubmitting(false);
    }
  };

  const handleDownloadPdf = async (devis) => {
    try {
      const res = await axios.get(`${API}/legalcoop/finance/devis/${devis.id}/pdf`, { headers: getAuthHeader(), responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${devis.quote_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`PDF ${devis.quote_number} téléchargé`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur téléchargement PDF');
    }
  };

  const handleSend = async (devis) => {
    try {
      const res = await axios.post(
        `${API}/legalcoop/finance/devis/${devis.id}/send`,
        {},
        { headers: { ...getAuthHeader(), Origin: window.location.origin } },
      );
      setLinkResult({ checkout_url: res.data?.checkout_url, devis: res.data?.devis });
      toast.success(`Devis ${devis.quote_number} envoyé`);
      fetchList();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur envoi devis');
    }
  };

  const handleAccept = async (devis) => {
    if (!window.confirm(`Marquer le devis ${devis.quote_number} comme accepté ?`)) return;
    try {
      await axios.post(`${API}/legalcoop/finance/devis/${devis.id}/accept`, {}, { headers: getAuthHeader() });
      toast.success(`Devis ${devis.quote_number} accepté`);
      fetchList();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur acceptation');
    }
  };

  const handleConvert = async (devis) => {
    if (!window.confirm(`Convertir ${devis.quote_number} en facture (création d'une demande LEGALCOOP payée) ?`)) return;
    try {
      const res = await axios.post(`${API}/legalcoop/finance/devis/${devis.id}/convert-to-invoice`, {}, { headers: getAuthHeader() });
      toast.success('Devis converti en facture');
      if (res.data?.request_id) {
        const url = `${API}/legalcoop/requests/${res.data.request_id}/facture-pdf`;
        toast.success(`Facture émise — request_id=${res.data.request_id.slice(0, 8)}`);
        void url;
      }
      fetchList();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur conversion');
    }
  };

  const handleCancel = async (devis) => {
    const reason = window.prompt(`Motif de l'annulation du devis ${devis.quote_number} ?`);
    if (reason === null) return;
    try {
      await axios.post(`${API}/legalcoop/finance/devis/${devis.id}/cancel`, {}, { params: { reason: reason || 'Non précisé' }, headers: getAuthHeader() });
      toast.success(`Devis ${devis.quote_number} annulé`);
      fetchList();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur annulation');
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Lien copié');
    } catch {
      toast.error('Impossible de copier');
    }
  };

  const totalDevis = items.length;
  const totalHt = items.reduce((s, d) => s + (d.amount_ht || 0), 0);

  const [fiscal, setFiscal] = useState(null);
  const [fiscalLoading, setFiscalLoading] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setFiscalLoading(true);
      try {
        const res = await axios.get(`${API}/legalcoop/finance/registre-fiscal-summary`, { headers: getAuthHeader() });
        if (!cancelled) setFiscal(res.data);
      } catch {
        // silence
      } finally {
        if (!cancelled) setFiscalLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const [avoirs, setAvoirs] = useState([]);
  const [recus, setRecus] = useState([]);
  const [kpi, setKpi] = useState(null);
  const [exportingCsv, setExportingCsv] = useState(false);

  const fetchAvoirs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/legalcoop/finance/avoirs?limit=100`, { headers: getAuthHeader() });
      setAvoirs(res.data?.items || []);
    } catch {}
  }, []);
  const fetchRecus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/legalcoop/finance/recus?limit=100`, { headers: getAuthHeader() });
      setRecus(res.data?.items || []);
    } catch {}
  }, []);
  const fetchKpi = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/legalcoop/finance/kpi-dashboard`, { headers: getAuthHeader() });
      setKpi(res.data);
    } catch {}
  }, []);
  useEffect(() => { fetchAvoirs(); fetchRecus(); fetchKpi(); }, [fetchAvoirs, fetchRecus, fetchKpi]);

  const downloadDocPdf = async (kind, id, number) => {
    try {
      const url = kind === 'avoir'
        ? `${API}/legalcoop/finance/avoirs/${id}/pdf`
        : `${API}/legalcoop/finance/recus/${id}/pdf`;
      const res = await axios.get(url, { headers: getAuthHeader(), responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const u = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = u; a.download = `${number}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(u);
      toast.success(`PDF ${number} téléchargé`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur téléchargement');
    }
  };

  const downloadCsv = async () => {
    setExportingCsv(true);
    try {
      const res = await axios.get(`${API}/legalcoop/finance/export-csv`, { headers: getAuthHeader(), responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8' });
      const u = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = u;
      a.download = `LEGALCOOP_export_${new Date().getFullYear()}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(u);
      toast.success('Export CSV téléchargé');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur export CSV');
    } finally {
      setExportingCsv(false);
    }
  };

  return {
    loading, items, counters, filterStatus, setFilterStatus,
    createOpen, setCreateOpen, createEmail, setCreateEmail, createName, setCreateName,
    createCcmiRef, setCreateCcmiRef, createNotes, setCreateNotes, createValidity, setCreateValidity, createSubmitting,
    linkResult, setLinkResult, fetchList, submitCreate, handleDownloadPdf, handleSend, handleAccept, handleConvert, handleCancel, copyToClipboard,
    totalDevis, totalHt, fiscal, fiscalLoading, avoirs, recus, kpi, exportingCsv, downloadDocPdf, downloadCsv,
  };
}
